import { useState } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import MarkdownEditor from '../MarkdownEditor/MarkdownEditor';
import SharedTextService from '../../../sdk/services/SharedTextService';
import { SharedText } from '../../../sdk/@types/Acertei';

export interface SharedTextEditorModalProps {
  open: boolean;
  /** Texto em edição. `null`/`undefined` abre em modo de criação. */
  text?: SharedText | null;
  onCancel: () => void;
  onSaved: (text: SharedText) => void;
  /**
   * `false` = modo local: não chama a API, só devolve os valores por `onSaved`.
   *
   * É o que permite o bulk import reusar este formulário — lá o texto ainda não
   * existe no banco, e só é criado quando a importação roda. Sem isso a tela de
   * importação precisaria de um formulário próprio, divergente deste.
   */
  persist?: boolean;
}

type FormValues = {
  title: string;
  source?: string;
  body: string;
};

/**
 * Formulário de texto base, compartilhado pela view administrativa
 * (`/admin/textos`) e pelo picker do cadastro de questão.
 */
export default function SharedTextEditorModal({
  open,
  text,
  onCancel,
  onSaved,
  persist = true,
}: SharedTextEditorModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  // No modo local o texto ainda não tem id (o do bulk import é um tempId), então
  // a presença do objeto é o que indica edição — senão o modal diria "Criar"
  // enquanto edita.
  const editing = persist ? Boolean(text?.id) : Boolean(text);

  /**
   * Valores iniciais em vez de `setFieldsValue` num efeito.
   *
   * Com `destroyOnClose` + `preserve={false}`, os campos não estão montados
   * quando o efeito de abertura roda: o `setFieldsValue` escrevia em campos
   * inexistentes e os valores eram descartados — era por isso que Título e
   * Fonte voltavam vazios ao abrir a edição.
   *
   * `initialValues` é aplicado na montagem, que é exatamente quando o modal
   * recria o formulário. Também evita reescrever o formulário a cada render
   * quando o chamador passa um objeto novo (o bulk import passa), o que
   * apagaria o que o usuário estivesse digitando.
   */
  const initialValues: FormValues = {
    title: text?.title ?? '',
    source: text?.source ?? '',
    body: text?.body ?? '',
  };

  const handleOk = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // antd já destaca os campos inválidos
    }

    const payload = {
      title: values.title.trim(),
      body: values.body,
      source: values.source?.trim() || undefined,
    };

    // Modo local: nada de rede e nada de notificação — quem chamou decide o
    // que fazer com os valores (no bulk import, guardar no estado da tela).
    if (!persist) {
      onSaved({ ...(text ?? ({} as SharedText)), ...payload, id: text?.id ?? '' });
      return;
    }

    try {
      setSaving(true);
      const saved = text?.id
        ? await SharedTextService.update(text.id, payload)
        : await SharedTextService.create(payload);

      notification.success({
        message: editing ? 'Texto base atualizado' : 'Texto base criado',
      });
      onSaved(saved);
    } catch {
      notification.error({ message: 'Não foi possível salvar o texto base.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={editing ? 'Salvar' : 'Criar'}
      cancelText="Cancelar"
      width={860}
      destroyOnClose
      title={editing ? 'Editar texto base' : 'Novo texto base'}
    >
      {/* A `key` recria o formulário quando o texto editado muda — necessário
          caso o modal siga montado entre duas edições diferentes, já que
          `initialValues` só vale na montagem. */}
      <Form
        key={text?.id || (text?.title ?? 'novo')}
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initialValues}
      >
        <Form.Item
          label="Título"
          name="title"
          rules={[{ required: true, message: 'Informe um título (ex.: Texto I)' }]}
        >
          <Input placeholder="Ex.: Texto I — diálogo de atendimento" maxLength={160} />
        </Form.Item>

        <Form.Item label="Fonte" name="source">
          <Input placeholder="Ex.: Adaptado de Machado de Assis, Dom Casmurro" maxLength={240} />
        </Form.Item>

        <Form.Item
          label="Texto"
          name="body"
          rules={[{ required: true, message: 'O texto não pode ficar vazio' }]}
        >
          <MarkdownEditor />
        </Form.Item>
      </Form>
    </Modal>
  );
}
